import Modal from 'components/Modal'
import Spinner from 'components/Spinner'
import Table from 'components/Table'
import { DiscardedAppointment as DiscardedAppointmentModel } from 'models/appointment.model'
import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppState } from 'store'
import { appointmentActions } from 'store/slices/appointments/appointments.slice'
import FacilityFormTemplate from 'templates/FacilityFormTemplate'
import {
  DiscardedAppointmentContainer,
  DiscardedAppointmentModalButton,
  DiscardedAppointmentModalContainer,
  DiscardedAppointmentModalContent,
  DiscardedAppointmentModalDes,
  DiscardedAppointmentModalFooter,
} from './DiscardedAppointment.styled'
import DiscardedAppointmentItem from './DiscardedAppointmentItem'
import toast from 'cogo-toast'
import { useParams } from 'react-router'
import { Location } from 'models/location'
import { useAmplitude } from 'hooks/useAmplitude'
import { AMPLITUDE_EVENTS } from 'amplitude/events'

const DISCARDED_APPOINTMENT_HEADER: string[] = [
  'Date/Time Discarded',
  'Discarding User',
  'Appointment User/Program',
  'Appointment Date/Time',
  'Options',
]

const DiscardedAppointment: React.FC = () => {
  const params: Location = useParams()
  const dispatch = useAppDispatch()
  const { discarded, loading, totalAppointments, currentPage } = useAppState(
    state => state.appointments
  )
  const [tableLoading, setTableLoading] = useState<boolean>(false)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [discardedList, setDiscardedList] = useState<
    DiscardedAppointmentModel[]
  >([])
  const [
    currentAppointment,
    setCurrentAppointment,
  ] = useState<DiscardedAppointmentModel | null>()
  const { logEvent } = useAmplitude()

  useEffect(() => {
    logEvent(AMPLITUDE_EVENTS.DISCARDED_APPOINTMENTS_PAGE_EVENT, {
      location: params.locationId,
    })
  }, [])

  useEffect(() => {
    if (discarded && discarded.length) {
      console.log('discarded', discarded)
      const discardedCopy = [...discarded].sort((a, b) => {
        const dateA = new Date(a.discarded_at).getTime()
        const dateB = new Date(b.discarded_at).getTime()
        return dateB - dateA
      })
      setDiscardedList(discardedCopy)
    }
  }, [discarded])

  useEffect(() => {
    (async () => {
      await dispatch(
        appointmentActions.getDiscardedAppointments({
          locationId: params ? params.locationId : '',
          limit: 10,
          page: currentPage,
        })
      )
    })()
  }, [currentPage])

  useEffect(() => {
    if (loading === 'loading') {
      setTableLoading(true)
    }
    if (loading === 'succeeded') {
      setTableLoading(false)
    }
  }, [loading])

  const handleRestore = async () => {
    setShowModal(false)
    if (currentAppointment) {
      await dispatch(
        appointmentActions.postRestoreAppointment({
          locationId: '1',
          undiscard: true,
          id: currentAppointment.id,
        })
      )
        .then(async () => {
          toast.success('Appointment successfully restored!', {
            position: 'top-right',
          })
          await dispatch(
            appointmentActions.getDiscardedAppointments({
              locationId: '1',
              limit: 10,
              page: 1,
            })
          )
        })
        .catch(() =>
          toast.error('We found an error. Please try again.', {
            position: 'top-right',
          })
        )
      setCurrentAppointment(null)
    }
  }

  const handlePage = (newPage: number) => {
    dispatch(appointmentActions.setCurrentPage(newPage))
  }

  return (
    <FacilityFormTemplate>
      <DiscardedAppointmentContainer>
        {tableLoading ? (
          <Spinner loading={tableLoading} />
        ) : (
          <Table
            header={DISCARDED_APPOINTMENT_HEADER}
            pagination
            totalCount={totalAppointments}
            currentPage={currentPage}
            onChangePage={handlePage}>
            {discardedList &&
              discardedList.length > 0 &&
              discardedList.map((appointment, index) => (
                <DiscardedAppointmentItem
                  key={`appointment_${index}`}
                  discarded={appointment}
                  onRestore={() => {
                    setShowModal(true)
                    setCurrentAppointment(appointment)
                  }}
                />
              ))}
          </Table>
        )}
      </DiscardedAppointmentContainer>

      {showModal && (
        <Modal>
          <DiscardedAppointmentModalContainer
            title={'Restore Confirmation'}
            onClose={() => {
              setShowModal(false)
              setCurrentAppointment(null)
            }}>
            <DiscardedAppointmentModalContent>
              <DiscardedAppointmentModalDes>
                Are you sure that you want to restore this discarded
                appointment?
              </DiscardedAppointmentModalDes>
              <DiscardedAppointmentModalFooter>
                <DiscardedAppointmentModalButton
                  size="medium"
                  text="Cancel"
                  variant="secondary"
                  preset="filled"
                  onClick={() => setShowModal(false)}
                />
                <DiscardedAppointmentModalButton
                  size="medium"
                  text="Restore"
                  variant="primary"
                  preset="filled"
                  onClick={handleRestore}
                />
              </DiscardedAppointmentModalFooter>
            </DiscardedAppointmentModalContent>
          </DiscardedAppointmentModalContainer>
        </Modal>
      )}
    </FacilityFormTemplate>
  )
}

export default DiscardedAppointment
